import { parsePlayerAwards } from './achievementAwards.ts';
/**
 * StoragePort backed by the DCC sheets API (POST /api/sheets), for a player
 * who has connected their code. The server is the only copy of the sheet.
 *
 * - `load()` fetches every document this player owns. Reads are then served
 *   from that snapshot, so the sheet renders without a network call per click.
 * - Every write is sent immediately, one at a time, naming the version it was
 *   based on. If someone else (another device, the GM) saved first, the server
 *   refuses the write and nothing changes; the caller reloads and retries.
 * - A play action that changes several records (Character + Session + Runtime)
 *   goes out as ONE save through `writeTogether`, so it lands whole or not at
 *   all. No journal is written for it.
 *
 * Failures always throw a SheetsSaveError; nothing is dropped silently.
 */
import type { StoragePort } from './storagePort.ts';

export type SheetsKind = 'character' | 'session' | 'runtime' | 'journal' | 'custom' | 'index';

export type SheetsFailure = 'rule' | 'inventory' | 'stale' | 'combat-stale' | 'pet-hidden' | 'pet-not-active' | 'pet-not-bonded' | 'pet-dead' | 'id-in-use' | 'event-applied' | 'event-cancelled' | 'auth' | 'offline' | 'server';

export class SheetsSaveError extends Error {
  constructor(message: string, readonly reason: SheetsFailure, readonly status = 0) {
    super(message);
    this.name = 'SheetsSaveError';
  }
}

/** Sends one action to the sheets API; the room and code are already bound. */
export type SheetsPoster = (body: Record<string, unknown>) => Promise<{ status: number; json: unknown }>;

export function fetchPoster(code: string, roomId: string): SheetsPoster {
  return async (body) => {
    const response = await fetch('/api/sheets', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${code.trim()}` },
      body: JSON.stringify({ ...body, roomId }),
      cache: 'no-store',
    });
    let json: unknown = null;
    try { json = await response.json(); } catch { json = null; }
    return { status: response.status, json };
  };
}

export type PartyEventKind = 'pass-two-hours' | 'rest-mend-hour' | 'rest-short' | 'rest-long' | 'rest-full-day' | 'combat-round' | 'end-of-floor';

export interface PartyEvent {
  eventId: string;
  kind: PartyEventKind;
  characterId: string;
  createdAt: number;
}

export interface RoomState {
  floor: number;
  version: number;
  createdAt: number;
  updatedAt: number;
  updatedBy: string;
}

export type CombatPhase = 'mob-declaration' | 'crawler-reaction' | 'mob-resolution' | 'crawler-action';
export type CombatHealthVisibility = 'hidden' | 'bar-only' | 'percent' | 'exact' | 'condition-only';

export interface CombatParticipant {
  participantId: string;
  kind: 'crawler' | 'mob' | 'pet';
  label: string;
  playerLabel: string | null;
  characterId: string | null;
  mine: boolean;
  sourceId: string | null;
  order: number;
  action1Used: boolean;
  action2Used: boolean;
  /** Derived count retained for GM/legacy views. */
  actionsUsed: number;
  movementUsed: boolean;
  evadeUsed: boolean;
  petState: 'waiting' | 'active' | 'hidden' | null;
  petAttackLimit: 1 | 2 | null;
  petAttacksUsed: number;
  healthSlotsTotal: number | null;
  healthSlotsRemaining: number | null;
  healthVisibility: CombatHealthVisibility;
  playerVisible?: boolean;
  conditions?: { name: string; visible: boolean }[];
  version: number;
}

export interface RoomCombat {
  combatId: string;
  encounterId: string;
  encounterName: string;
  round: number;
  phase: CombatPhase;
  version: number;
  participants: CombatParticipant[];
  viewerHasCrawler?: boolean;
}

/** One row of the shared Party Inventory, already named by the server. */
export interface PartyInventoryEntry {
  itemId: string;
  quantity: number;
  name: string;
  source: 'core' | 'room' | 'unknown';
}

/**
 * The room's shared items as a player may see them: the Item definitions that
 * belong to the room (so a transferred or GM-awarded Item means something on
 * this sheet) and the Party Inventory. Definitions are `unknown` here on
 * purpose - they are checked by the engine's validator before any is trusted
 * (`usableRoomItems`).
 */
export interface RoomInventory {
  awards?: import('./achievementAwards.ts').PlayerAward[];
  roomItems: unknown[];
  /** Definition versions used to refuse activation from an old GM edit. */
  roomItemVersions: Record<string, number>;
  /**
   * The Skill and Attack halves of GM-made weapons this player can see or has
   * learned. `unknown` for the same reason `roomItems` is: checked by the
   * engine's own library validator before use (`usableRoomWeapons`).
   */
  roomSkills: unknown[];
  roomAttacks: unknown[];
  /** Charges and cooldown of Items that keep them, as of the last read. Keyed by Item ID. */
  powerStates: Record<string, ItemPowerStatusView>;
  party: PartyInventoryEntry[];
  /** True while a room combat is active: the stash is closed to players. */
  combatActive: boolean;
  inventoryRevision: number;
  /** In-game seconds the GM has passed; charges are worked out against it. */
  itemClock: number;
}

/** What the server says about one Item's charges and cooldown right now. */
export interface ItemPowerStatusView {
  charges: number | null;
  maxCharges: number | null;
  secondsToNextCharge: number | null;
  cooldownSecondsRemaining: number;
  /** Why it cannot be activated, or null when it can. */
  notReady: string | null;
}

function parsePowerStates(value: unknown): Record<string, ItemPowerStatusView> {
  const out: Record<string, ItemPowerStatusView> = {};
  for (const [itemId, entry] of Object.entries(record(value))) {
    const row = record(entry);
    const whole = (v: unknown): number | null => (Number.isInteger(v) && Number(v) >= 0 ? Number(v) : null);
    out[itemId] = {
      charges: whole(row.charges),
      maxCharges: whole(row.maxCharges),
      secondsToNextCharge: whole(row.secondsToNextCharge),
      cooldownSecondsRemaining: whole(row.cooldownSecondsRemaining) ?? 0,
      notReady: typeof row.notReady === 'string' ? row.notReady : null,
    };
  }
  return out;
}

const INVENTORY_REFUSALS = new Set([
  'not-free', 'not-enough', 'not-owned', 'combat-active', 'inventory-busy', 'unknown-item',
  'weapon-not-shareable', 'too-many', 'no-crawler', 'unreadable-crawler', 'invalid-item',
  'power-not-ready', 'no-power', 'inventory-mismatch', 'not-room-item',
]);

export function parseRoomInventory(value: unknown): RoomInventory | null {
  const raw = record(value);
  if (!Array.isArray(raw.roomItems) || !Array.isArray(raw.party) || !Number.isInteger(raw.inventoryRevision)) return null;
  const party: PartyInventoryEntry[] = [];
  for (const entry of raw.party) {
    const row = record(entry);
    if (typeof row.itemId !== 'string' || !Number.isInteger(row.quantity) || Number(row.quantity) < 1) return null;
    party.push({
      itemId: row.itemId,
      quantity: Number(row.quantity),
      name: typeof row.name === 'string' && row.name ? row.name : row.itemId,
      source: row.source === 'core' || row.source === 'room' ? row.source : 'unknown',
    });
  }
  return {
    ...(Array.isArray(raw.awards) ? { awards: parsePlayerAwards(raw.awards) } : {}),
    roomItems: raw.roomItems,
    roomItemVersions: Object.fromEntries(Object.entries(record(raw.roomItemVersions)).filter(([, v]) => Number.isInteger(v) && Number(v) >= 1)) as Record<string, number>,
    roomSkills: Array.isArray(raw.roomSkills) ? raw.roomSkills : [],
    roomAttacks: Array.isArray(raw.roomAttacks) ? raw.roomAttacks : [],
    powerStates: parsePowerStates(raw.powerStates),
    party,
    combatActive: raw.combatActive === true,
    inventoryRevision: Number(raw.inventoryRevision),
    itemClock: Number.isInteger(raw.itemClock) && Number(raw.itemClock) >= 0 ? Number(raw.itemClock) : 0,
  };
}

export interface SheetsApiStorage extends StoragePort {
  /** The shared Party Inventory and room Item definitions, from the server. */
  getRoomInventory(): Promise<RoomInventory>;
  /**
   * True when the room's items changed since `getRoomInventory` last ran. No
   * network call: the revision arrives with the room-state poll.
   */
  roomInventoryChanged(): boolean;
  /**
   * Move FREE copies from one of this player's crawlers into the Party
   * Inventory. The server does the arithmetic and changes the crawler record
   * itself, so the caller MUST `load()` afterwards before reading or saving.
   */
  depositItem(characterId: string, itemId: string, quantity: number): Promise<{ itemName: string; inventory: RoomInventory }>;
  /** Move copies from the Party Inventory to one of this player's crawlers. `load()` afterwards. */
  claimItem(characterId: string, itemId: string, quantity: number): Promise<{ itemName: string; inventory: RoomInventory }>;
  /**
   * Activate an Item Power. `changes` is what the rules engine worked out for
   * the crawler; the server spends the Item's charge and saves both in one
   * transaction, or refuses and saves neither.
   */
  activateItem(
    characterId: string,
    itemId: string,
    changes: { character?: unknown; runtime?: unknown },
  ): Promise<{ inventory: RoomInventory }>;
  /** Fetch this player's documents from the server, replacing the snapshot. */
  load(): Promise<void>;
  /** Shared room combat tracker. Player access exposes only their own crawler identity. */
  getCombat(): Promise<RoomCombat | null>;
  /** Public HUD only: excludes every GM-hidden participant, including our own. */
  getCombatTracker(): Promise<RoomCombat | null>;
  /** Updates this player's Action / Movement dots for the named crawler. */
  setCombatTracker(combatId: string, characterId: string, action1Used: boolean, action2Used: boolean, movementUsed: boolean, expectedVersion: number): Promise<RoomCombat>;
  /** Add one/two Action marks after a successful sheet Action. Never blocks the sheet action itself. */
  spendCombatActions(combatId: string, characterId: string, count: 1 | 2, expectedVersion: number): Promise<{ combat: RoomCombat; actionsApplied: number; outOfActions: boolean }>;
  /** Mark the round's Evade charge once; repeated Evades are idempotent. */
  recordCombatEvade(combatId: string, characterId: string, expectedVersion: number): Promise<{ combat: RoomCombat; actionApplied: boolean; alreadyRecorded: boolean; outOfActions: boolean }>;
  /** Resolve the crawler's Animal Handling Action into this companion's combat state. */
  recordPetCombatEntry(combatId: string, characterId: string, petId: string, degree: string, expectedCrawlerVersion: number, expectedPetVersion: number): Promise<{ combat: RoomCombat; outcome: 'joined' | 'frenzied' | 'retry' | 'hidden'; actionApplied: boolean; alreadyResolved: boolean; outOfActions: boolean }>;
  /** Manual pet A1/A2/Movement bookkeeping in the shared tracker. */
  setPetCombatTracker(combatId: string, characterId: string, petId: string, action1Used: boolean, action2Used: boolean, movementUsed: boolean, expectedVersion: number): Promise<RoomCombat>;
  /** Spend one pet Action and one per-round Attack allowance atomically. */
  spendPetCombatAttack(combatId: string, characterId: string, petId: string, expectedVersion: number): Promise<{ combat: RoomCombat; actionApplied: boolean; attackApplied: boolean; attackLimitReached: boolean; outOfActions: boolean }>;
  /** Spend one pet Action plus optional Movement / Attack allowance atomically. */
  spendPetCombatResources(combatId: string, characterId: string, petId: string, expectedVersion: number, resources: { attack?: boolean; movement?: boolean }): Promise<{ combat: RoomCombat; actionApplied: boolean; attackApplied: boolean; movementApplied: boolean; attackLimitReached: boolean; movementAlreadyUsed: boolean; outOfActions: boolean }>;
  /** Mark the pet's round Evade charge once; repeated Evades are idempotent. */
  recordPetCombatEvade(combatId: string, characterId: string, petId: string, expectedVersion: number): Promise<{ combat: RoomCombat; actionApplied: boolean; alreadyRecorded: boolean; outOfActions: boolean }>;
  /** True when the GM-authoritative room state differs from our loaded snapshot. */
  checkRoomState(): Promise<boolean>;
  /** The room state from the most recent full load. Null means the GM has not initialized it yet. */
  getCachedRoomState(): RoomState | null;
  /** Pending GM-triggered time/rest events for this player's snapshotted crawler. */
  listPartyEvents(): Promise<PartyEvent[]>;
  /** Atomically save a party event's Character/Session/Runtime changes and mark it applied. */
  applyPartyEvent(eventId: string, characterId: string, changes: { character?: unknown; session?: unknown; runtime: unknown }): Promise<void>;
}

interface Entry { version: number; data: unknown }

const CHARACTER_LANES: SheetsKind[] = ['character', 'session', 'runtime', 'journal'];

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function parseRoomState(value: unknown): RoomState | null {
  if (value === null) return null;
  const state = record(value);
  if (
    !Number.isInteger(state.floor) || Number(state.floor) < 1 || Number(state.floor) > 18 ||
    !Number.isInteger(state.version) || Number(state.version) < 1 ||
    typeof state.createdAt !== 'number' || typeof state.updatedAt !== 'number' || typeof state.updatedBy !== 'string'
  ) return null;
  return {
    floor: Number(state.floor), version: Number(state.version),
    createdAt: state.createdAt, updatedAt: state.updatedAt, updatedBy: state.updatedBy,
  };
}

function sameRoomState(a: RoomState | null, b: RoomState | null): boolean {
  return a?.version === b?.version && a?.floor === b?.floor && (a !== null) === (b !== null);
}

/** Parse the shared room combat payload used by both player and GM viewers. */
export function parseRoomCombat(value: unknown): RoomCombat | null {
  if (value === null) return null;
  const combat = record(value);
  const phases: CombatPhase[] = ['mob-declaration', 'crawler-reaction', 'mob-resolution', 'crawler-action'];
  if (
    typeof combat.combatId !== 'string' ||
    typeof combat.encounterId !== 'string' ||
    typeof combat.encounterName !== 'string' ||
    !Number.isInteger(combat.round) || Number(combat.round) < 1 ||
    typeof combat.phase !== 'string' || !phases.includes(combat.phase as CombatPhase) ||
    !Number.isInteger(combat.version) || Number(combat.version) < 1 ||
    !Array.isArray(combat.participants)
  ) return null;
  const participants: CombatParticipant[] = [];
  for (const raw of combat.participants) {
    const participant = record(raw);
    // Pet combat fields were added in Sheets schema 9. Default them for
    // crawler/mob payloads so a mixed-version UI does not reject otherwise
    // valid participants during the coordinated deployment window.
    const petState = participant.petState ?? null;
    const petAttackLimit = participant.petAttackLimit ?? null;
    const petAttacksUsed = participant.petAttacksUsed ?? 0;
    if (
      typeof participant.participantId !== 'string' ||
      (participant.kind !== 'crawler' && participant.kind !== 'mob' && participant.kind !== 'pet') ||
      typeof participant.label !== 'string' ||
      (participant.playerLabel !== null && typeof participant.playerLabel !== 'string') ||
      (participant.characterId !== null && typeof participant.characterId !== 'string') ||
      typeof participant.mine !== 'boolean' ||
      (participant.sourceId !== null && typeof participant.sourceId !== 'string') ||
      !Number.isInteger(participant.order) || Number(participant.order) < 0 ||
      typeof participant.action1Used !== 'boolean' ||
      typeof participant.action2Used !== 'boolean' ||
      !Number.isInteger(participant.actionsUsed) || Number(participant.actionsUsed) < 0 || Number(participant.actionsUsed) > 2 ||
      typeof participant.movementUsed !== 'boolean' ||
      typeof participant.evadeUsed !== 'boolean' ||
      (petState !== null && petState !== 'waiting' && petState !== 'active' && petState !== 'hidden') ||
      (petAttackLimit !== null && petAttackLimit !== 1 && petAttackLimit !== 2) ||
      !Number.isInteger(petAttacksUsed) || Number(petAttacksUsed) < 0 || Number(petAttacksUsed) > 2 ||
      (participant.healthSlotsTotal !== null && (!Number.isInteger(participant.healthSlotsTotal) || Number(participant.healthSlotsTotal) < 1)) ||
      (participant.healthSlotsRemaining !== null && (!Number.isInteger(participant.healthSlotsRemaining) || Number(participant.healthSlotsRemaining) < 0)) ||
      typeof participant.healthVisibility !== 'string' ||
      !(['hidden', 'bar-only', 'percent', 'exact', 'condition-only'] as string[]).includes(participant.healthVisibility) ||
      !Number.isInteger(participant.version) || Number(participant.version) < 1
    ) return null;
    if (participant.healthSlotsTotal !== null && participant.healthSlotsRemaining !== null && Number(participant.healthSlotsRemaining) > Number(participant.healthSlotsTotal)) return null;
    if (participant.kind === 'pet' && (petState === null || petAttackLimit !== null && petAttackLimit !== 1 && petAttackLimit !== 2)) return null;
    participants.push({
      participantId: participant.participantId,
      kind: participant.kind,
      label: participant.label,
      playerLabel: participant.playerLabel as string | null,
      characterId: participant.characterId as string | null,
      mine: participant.mine,
      sourceId: participant.sourceId as string | null,
      order: Number(participant.order),
      action1Used: participant.action1Used,
      action2Used: participant.action2Used,
      actionsUsed: Number(participant.actionsUsed),
      movementUsed: participant.movementUsed,
      evadeUsed: participant.evadeUsed,
      petState: petState as 'waiting' | 'active' | 'hidden' | null,
      petAttackLimit: petAttackLimit as 1 | 2 | null,
      petAttacksUsed: Number(petAttacksUsed),
      healthSlotsTotal: participant.healthSlotsTotal === null ? null : Number(participant.healthSlotsTotal),
      healthSlotsRemaining: participant.healthSlotsRemaining === null ? null : Number(participant.healthSlotsRemaining),
      healthVisibility: participant.healthVisibility as CombatHealthVisibility,
      playerVisible: participant.playerVisible !== false,
      conditions: Array.isArray(participant.conditions) ? participant.conditions.filter((c: any) => typeof c?.name === 'string' && typeof c?.visible === 'boolean') as { name: string; visible: boolean }[] : [],
      version: Number(participant.version),
    });
  }
  return {
    combatId: combat.combatId, encounterId: combat.encounterId, encounterName: combat.encounterName,
    round: Number(combat.round), phase: combat.phase as CombatPhase, version: Number(combat.version), participants,
    viewerHasCrawler: combat.viewerHasCrawler === true,
  };
}

function failure(status: number, json: unknown): SheetsSaveError {
  const body = record(json);
  const serverMessage = typeof body.error === 'string' ? body.error : '';
  if (status === 401 || status === 403) {
    return new SheetsSaveError('Your player code was replaced or revoked. Ask your GM for your current code.', 'auth', status);
  }
  if (status === 409 && (body.reason === 'combat-stale' || body.reason === 'combat-participant-stale')) {
    return new SheetsSaveError('The combat tracker changed somewhere else. Refreshed; try again.', 'combat-stale', status);
  }
  // The Party Inventory explains its own refusals ("only 1 is free...").
  if (typeof body.reason === 'string' && INVENTORY_REFUSALS.has(body.reason)) {
    return new SheetsSaveError(serverMessage || 'That item could not be moved.', 'inventory', status);
  }
  if (status === 409 && body.reason === 'pet-hidden') return new SheetsSaveError(serverMessage || 'That companion is hiding for the rest of this combat.', 'pet-hidden', status);
  if (status === 409 && body.reason === 'pet-not-active') return new SheetsSaveError(serverMessage || 'That companion is not currently fighting.', 'pet-not-active', status);
  if (status === 409 && body.reason === 'pet-not-bonded') return new SheetsSaveError(serverMessage || 'Only a Bonded companion can enter combat.', 'pet-not-bonded', status);
  if (status === 409 && body.reason === 'pet-dead') return new SheetsSaveError(serverMessage || 'A dead companion cannot enter combat.', 'pet-dead', status);
  if (status === 409 && body.reason === 'id-in-use') {
    return new SheetsSaveError('That crawler ID is already used in this room.', 'id-in-use', status);
  }
  if (status === 409 && body.reason === 'event-applied') {
    return new SheetsSaveError('That GM time/rest event was already applied on another open copy of this sheet.', 'event-applied', status);
  }
  if (status === 409 && body.reason === 'event-cancelled') {
    return new SheetsSaveError('That GM time/rest event targeted a crawler that has since been deleted.', 'event-cancelled', status);
  }
  if (status === 409 && ['advancement-floor', 'advancement-combat', 'attribute-budget', 'attribute-combat'].includes(String(body.reason))) {
    return new SheetsSaveError(serverMessage || 'That attribute change is not allowed.', 'rule', status);
  }
  if (status === 409) {
    return new SheetsSaveError('This sheet was changed somewhere else (another device, or your GM).', 'stale', status);
  }
  return new SheetsSaveError(serverMessage || `The sheet server returned an error (${status}).`, 'server', status);
}

export function createSheetsApiStorage(post: SheetsPoster): SheetsApiStorage {
  const cache = new Map<string, Entry>();
  const slot = (kind: SheetsKind, key: string) => `${kind}\u0000${key}`;
  let loaded = false;
  let roomState: RoomState | null = null;
  // The last item movement that touched THIS player's crawlers, as of the
  // snapshot in `cache`. A newer one means the server changed a crawler record.
  let myInventoryRevision = 0;
  // The room's item revision: the newest one heard of, and the one the last
  // fetched Party Inventory snapshot reflects.
  let latestInventoryRevision = 0;
  let shownInventoryRevision = -1;
  // The room's Item clock, the same way: charges change when the GM passes
  // time, and that writes no ledger line for the revision to notice.
  let latestItemClock = 0;
  let shownItemClock = -1;
  let latestCombatActive: boolean | null = null;
  let shownCombatActive: boolean | null = null;
  let shownRoomItemVersions: Record<string, number> = {};
  let queue: Promise<unknown> = Promise.resolve();

  const revisionOf = (value: unknown): number | null => (Number.isInteger(value) && Number(value) >= 0 ? Number(value) : null);

  function adoptInventory(payload: Record<string, unknown>, status: number): RoomInventory {
    const inventory = parseRoomInventory(payload.inventory);
    if (inventory === null) throw new SheetsSaveError('The sheet server sent an unreadable Party Inventory.', 'server', status);
    latestInventoryRevision = Math.max(latestInventoryRevision, inventory.inventoryRevision);
    shownInventoryRevision = inventory.inventoryRevision;
    latestItemClock = Math.max(latestItemClock, inventory.itemClock);
    shownItemClock = inventory.itemClock;
    shownRoomItemVersions = { ...inventory.roomItemVersions };
    latestCombatActive = inventory.combatActive;
    shownCombatActive = inventory.combatActive;
    return inventory;
  }

  function transfer(action: 'player.depositItem' | 'player.claimItem', characterId: string, itemId: string, quantity: number) {
    return enqueue(async () => {
      const { status, json } = await send({ action, characterId, itemId, quantity });
      if (status !== 200) throw failure(status, json);
      const payload = record(json);
      const moved = record(payload.moved);
      return { itemName: typeof moved.name === 'string' ? moved.name : itemId, inventory: adoptInventory(payload, status) };
    });
  }

  async function send(body: Record<string, unknown>) {
    try {
      return await post(body);
    } catch {
      throw new SheetsSaveError('Could not reach the sheet server. Your last change was not saved.', 'offline');
    }
  }

  /** Writes run strictly one after another, so each names the latest version. */
  function enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = queue.then(task, task);
    queue = run.catch(() => undefined);
    return run;
  }

  function read(kind: SheetsKind, key: string): unknown | null {
    if (!loaded) throw new Error('Sheet storage was read before load() finished.');
    const entry = cache.get(slot(kind, key));
    return entry ? structuredClone(entry.data) : null;
  }

  function put(kind: SheetsKind, key: string, data: unknown): Promise<void> {
    return enqueue(async () => {
      const expectedVersion = cache.get(slot(kind, key))?.version ?? 0;
      const { status, json } = await send({ action: 'player.save', writes: [{ op: 'put', kind, key, data, expectedVersion }] });
      if (status !== 200) throw failure(status, json);
      const payload = record(json);
      const rawRoomState = payload.roomState;
      const nextRoomState = parseRoomState(rawRoomState);
      if (rawRoomState !== null && rawRoomState !== undefined && nextRoomState === null) {
        throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
      }
      roomState = nextRoomState;
      const cachedData = kind === 'character' && roomState && data && typeof data === 'object' && !Array.isArray(data)
        ? { ...(data as Record<string, unknown>), floor: roomState.floor }
        : data;
      cache.set(slot(kind, key), { version: expectedVersion + 1, data: structuredClone(cachedData) });
    });
  }

  function remove(kind: SheetsKind, key: string): Promise<void> {
    return enqueue(async () => {
      const current = cache.get(slot(kind, key));
      if (!current) return;
      const { status, json } = await send({ action: 'player.save', writes: [{ op: 'delete', kind, key, expectedVersion: current.version }] });
      if (status !== 200) throw failure(status, json);
      const rawRoomState = record(json).roomState;
      const nextRoomState = parseRoomState(rawRoomState);
      if (rawRoomState !== null && rawRoomState !== undefined && nextRoomState === null) {
        throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
      }
      roomState = nextRoomState;
      const cleared = kind === 'character' ? CHARACTER_LANES : [kind];
      for (const lane of cleared) cache.delete(slot(lane, key));
    });
  }


  function readPartyEvent(value: unknown): PartyEvent | null {
    const event = record(value);
    const kinds: PartyEventKind[] = ['pass-two-hours', 'rest-mend-hour', 'rest-short', 'rest-long', 'rest-full-day', 'combat-round', 'end-of-floor'];
    if (
      typeof event.eventId !== 'string' ||
      typeof event.characterId !== 'string' ||
      typeof event.createdAt !== 'number' ||
      typeof event.kind !== 'string' ||
      !kinds.includes(event.kind as PartyEventKind)
    ) return null;
    return {
      eventId: event.eventId,
      characterId: event.characterId,
      createdAt: event.createdAt,
      kind: event.kind as PartyEventKind,
    };
  }

  function readCombat(value: unknown): RoomCombat | null {
    return parseRoomCombat(value);
  }

  function eventWrites(characterId: string, changes: { character?: unknown; session?: unknown; runtime: unknown }) {
    const writes: { op: 'put'; kind: 'character' | 'session' | 'runtime'; key: string; data: unknown; expectedVersion: number }[] = [];
    if (changes.character !== undefined) {
      writes.push({
        op: 'put', kind: 'character', key: characterId, data: changes.character,
        expectedVersion: cache.get(slot('character', characterId))?.version ?? 0,
      });
    }
    if (changes.session !== undefined) {
      writes.push({
        op: 'put', kind: 'session', key: characterId, data: changes.session,
        expectedVersion: cache.get(slot('session', characterId))?.version ?? 0,
      });
    }
    writes.push({
      op: 'put', kind: 'runtime', key: characterId, data: changes.runtime,
      expectedVersion: cache.get(slot('runtime', characterId))?.version ?? 0,
    });
    return writes;
  }

  return {
    load() {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.listDocuments' });
        if (status !== 200) throw failure(status, json);
        const payload = record(json);
        const documents = payload.documents;
        if (!Array.isArray(documents)) throw new SheetsSaveError('The sheet server sent an unreadable response.', 'server', status);
        const rawRoomState = payload.roomState;
        const nextRoomState = parseRoomState(rawRoomState);
        if (rawRoomState !== null && rawRoomState !== undefined && nextRoomState === null) {
          throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
        }
        roomState = nextRoomState;
        myInventoryRevision = revisionOf(payload.myInventoryRevision) ?? myInventoryRevision;
        latestInventoryRevision = Math.max(latestInventoryRevision, revisionOf(payload.inventoryRevision) ?? 0);
        latestItemClock = Math.max(latestItemClock, revisionOf(payload.itemClock) ?? 0);
        if (typeof payload.combatActive === 'boolean') latestCombatActive = payload.combatActive;
        cache.clear();
        for (const raw of documents) {
          const doc = record(raw);
          if (typeof doc.kind === 'string' && typeof doc.key === 'string' && typeof doc.version === 'number') {
            cache.set(slot(doc.kind as SheetsKind, doc.key), { version: doc.version, data: doc.data });
          }
        }
        loaded = true;
      });
    },

    checkRoomState() {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.getRoomState' });
        if (status !== 200) throw failure(status, json);
        const payload = record(json);
        const raw = payload.roomState;
        const remote = parseRoomState(raw);
        if (raw !== null && remote === null) throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
        latestInventoryRevision = Math.max(latestInventoryRevision, revisionOf(payload.inventoryRevision) ?? 0);
        latestItemClock = Math.max(latestItemClock, revisionOf(payload.itemClock) ?? 0);
        if (typeof payload.combatActive === 'boolean') latestCombatActive = payload.combatActive;
        // An item movement that touched this player's crawlers changed a
        // character record on the server (a GM award, a transfer from another
        // tab). That needs the same rebase a room-state change does.
        const mine = revisionOf(payload.myInventoryRevision);
        return !sameRoomState(roomState, remote) || (mine !== null && mine !== myInventoryRevision);
      });
    },

    getRoomInventory() {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.getRoomInventory' });
        if (status !== 200) throw failure(status, json);
        return adoptInventory(record(json), status);
      });
    },

    roomInventoryChanged() {
      return latestInventoryRevision !== shownInventoryRevision || latestItemClock !== shownItemClock || latestCombatActive !== shownCombatActive;
    },

    activateItem(characterId, itemId, changes) {
      return enqueue(async () => {
        const writes = (['character', 'runtime'] as const)
          .filter((kind) => changes[kind] !== undefined)
          .map((kind) => ({
            op: 'put' as const, kind, key: characterId, data: changes[kind],
            expectedVersion: cache.get(slot(kind, characterId))?.version ?? 0,
          }));
        const { status, json } = await send({
          action: 'player.activateItem', characterId, itemId, writes,
          expectedItemVersion: shownRoomItemVersions[itemId] ?? 0,
          expectedCharacterVersion: cache.get(slot('character', characterId))?.version ?? 0,
        });
        if (status !== 200) throw failure(status, json);
        const payload = record(json);
        const inventory = adoptInventory(payload, status);
        const remoteRoomState = parseRoomState(payload.roomState);
        if (payload.roomState !== null && remoteRoomState === null) throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
        roomState = remoteRoomState;
        // This use wrote a ledger line naming this player. It is OUR change and
        // the snapshot below already holds it, so it must not read as a
        // foreign edit and trigger a reload on the next poll.
        myInventoryRevision = revisionOf(record(payload.inventory).myInventoryRevision) ?? myInventoryRevision;
        for (const write of writes) {
          const data = write.kind === 'character' && roomState && write.data && typeof write.data === 'object' && !Array.isArray(write.data)
            ? { ...(write.data as Record<string, unknown>), floor: roomState.floor }
            : write.data;
          cache.set(slot(write.kind, characterId), { version: write.expectedVersion + 1, data: structuredClone(data) });
        }
        return { inventory };
      });
    },

    depositItem(characterId, itemId, quantity) { return transfer('player.depositItem', characterId, itemId, quantity); },
    claimItem(characterId, itemId, quantity) { return transfer('player.claimItem', characterId, itemId, quantity); },

    getCachedRoomState() {
      return roomState ? { ...roomState } : null;
    },

    getCombat() {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.getCombat' });
        if (status !== 200) throw failure(status, json);
        const raw = record(json).combat;
        const combat = readCombat(raw);
        if (raw !== null && combat === null) throw new SheetsSaveError('The sheet server sent an unreadable combat tracker.', 'server', status);
        return combat;
      });
    },

    getCombatTracker() {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.getCombatTracker' });
        if (status !== 200) throw failure(status, json);
        const raw = record(json).combat;
        const combat = readCombat(raw);
        if (raw !== null && combat === null) throw new SheetsSaveError('The sheet server sent an unreadable combat tracker.', 'server', status);
        return combat;
      });
    },

    setCombatTracker(combatId, characterId, action1Used, action2Used, movementUsed, expectedVersion) {
      return enqueue(async () => {
        const { status, json } = await send({
          action: 'player.setCombatTracker', combatId, characterId, action1Used, action2Used, movementUsed, expectedVersion,
        });
        if (status !== 200) throw failure(status, json);
        const combat = readCombat(record(json).combat);
        if (!combat) throw new SheetsSaveError('The sheet server sent an unreadable combat tracker.', 'server', status);
        return combat;
      });
    },

    spendCombatActions(combatId, characterId, count, expectedVersion) {
      return enqueue(async () => {
        const { status, json } = await send({
          action: 'player.spendCombatActions', combatId, characterId, count, expectedVersion,
        });
        if (status !== 200) throw failure(status, json);
        const body = record(json);
        const combat = readCombat(body.combat);
        if (!combat || !Number.isInteger(body.actionsApplied) || typeof body.outOfActions !== 'boolean') {
          throw new SheetsSaveError('The sheet server sent an unreadable combat Action response.', 'server', status);
        }
        return { combat, actionsApplied: Number(body.actionsApplied), outOfActions: body.outOfActions };
      });
    },

    recordCombatEvade(combatId, characterId, expectedVersion) {
      return enqueue(async () => {
        const { status, json } = await send({
          action: 'player.recordCombatEvade', combatId, characterId, expectedVersion,
        });
        if (status !== 200) throw failure(status, json);
        const body = record(json);
        const combat = readCombat(body.combat);
        if (!combat || typeof body.actionApplied !== 'boolean' || typeof body.alreadyRecorded !== 'boolean' || typeof body.outOfActions !== 'boolean') {
          throw new SheetsSaveError('The sheet server sent an unreadable Evade tracker response.', 'server', status);
        }
        return {
          combat,
          actionApplied: body.actionApplied,
          alreadyRecorded: body.alreadyRecorded,
          outOfActions: body.outOfActions,
        };
      });
    },


    recordPetCombatEntry(combatId, characterId, petId, degree, expectedCrawlerVersion, expectedPetVersion) {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.recordPetCombatEntry', combatId, characterId, petId, degree, expectedCrawlerVersion, expectedPetVersion });
        if (status !== 200) throw failure(status, json);
        const body = record(json);
        const combat = readCombat(body.combat);
        const outcomes = ['joined', 'frenzied', 'retry', 'hidden'];
        if (!combat || typeof body.outcome !== 'string' || !outcomes.includes(body.outcome) || typeof body.actionApplied !== 'boolean' || typeof body.alreadyResolved !== 'boolean' || typeof body.outOfActions !== 'boolean') {
          throw new SheetsSaveError('The sheet server sent an unreadable companion combat-entry response.', 'server', status);
        }
        return { combat, outcome: body.outcome as 'joined' | 'frenzied' | 'retry' | 'hidden', actionApplied: body.actionApplied, alreadyResolved: body.alreadyResolved, outOfActions: body.outOfActions };
      });
    },

    setPetCombatTracker(combatId, characterId, petId, action1Used, action2Used, movementUsed, expectedVersion) {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.setPetCombatTracker', combatId, characterId, petId, action1Used, action2Used, movementUsed, expectedVersion });
        if (status !== 200) throw failure(status, json);
        const combat = readCombat(record(json).combat);
        if (!combat) throw new SheetsSaveError('The sheet server sent an unreadable companion tracker.', 'server', status);
        return combat;
      });
    },

    spendPetCombatAttack(combatId, characterId, petId, expectedVersion) {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.spendPetCombatAttack', combatId, characterId, petId, expectedVersion });
        if (status !== 200) throw failure(status, json);
        const body = record(json);
        const combat = readCombat(body.combat);
        if (!combat || typeof body.actionApplied !== 'boolean' || typeof body.attackApplied !== 'boolean' || typeof body.attackLimitReached !== 'boolean' || typeof body.outOfActions !== 'boolean') {
          throw new SheetsSaveError('The sheet server sent an unreadable companion Attack response.', 'server', status);
        }
        return { combat, actionApplied: body.actionApplied, attackApplied: body.attackApplied, attackLimitReached: body.attackLimitReached, outOfActions: body.outOfActions };
      });
    },

    spendPetCombatResources(combatId, characterId, petId, expectedVersion, resources) {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.spendPetCombatResources', combatId, characterId, petId, expectedVersion, attack: resources.attack === true, movement: resources.movement === true });
        if (status !== 200) throw failure(status, json);
        const body = record(json);
        const combat = readCombat(body.combat);
        if (!combat || typeof body.actionApplied !== 'boolean' || typeof body.attackApplied !== 'boolean' || typeof body.movementApplied !== 'boolean' || typeof body.attackLimitReached !== 'boolean' || typeof body.movementAlreadyUsed !== 'boolean' || typeof body.outOfActions !== 'boolean') {
          throw new SheetsSaveError('The sheet server sent an unreadable companion resource-spend response.', 'server', status);
        }
        return { combat, actionApplied: body.actionApplied, attackApplied: body.attackApplied, movementApplied: body.movementApplied, attackLimitReached: body.attackLimitReached, movementAlreadyUsed: body.movementAlreadyUsed, outOfActions: body.outOfActions };
      });
    },

    recordPetCombatEvade(combatId, characterId, petId, expectedVersion) {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.recordPetCombatEvade', combatId, characterId, petId, expectedVersion });
        if (status !== 200) throw failure(status, json);
        const body = record(json);
        const combat = readCombat(body.combat);
        if (!combat || typeof body.actionApplied !== 'boolean' || typeof body.alreadyRecorded !== 'boolean' || typeof body.outOfActions !== 'boolean') {
          throw new SheetsSaveError('The sheet server sent an unreadable companion Evade response.', 'server', status);
        }
        return { combat, actionApplied: body.actionApplied, alreadyRecorded: body.alreadyRecorded, outOfActions: body.outOfActions };
      });
    },

    listPartyEvents() {
      return enqueue(async () => {
        const { status, json } = await send({ action: 'player.listPartyEvents' });
        if (status !== 200) throw failure(status, json);
        const events = record(json).events;
        if (!Array.isArray(events)) throw new SheetsSaveError('The sheet server sent an unreadable party-event response.', 'server', status);
        const parsed = events.map(readPartyEvent);
        if (parsed.some((event) => event === null)) throw new SheetsSaveError('The sheet server sent an unreadable party event.', 'server', status);
        return parsed as PartyEvent[];
      });
    },

    applyPartyEvent(eventId, characterId, changes) {
      return enqueue(async () => {
        const writes = eventWrites(characterId, changes);
        const { status, json } = await send({ action: 'player.applyPartyEvent', eventId, writes });
        if (status !== 200) throw failure(status, json);
        const rawRoomState = record(json).roomState;
        const nextRoomState = parseRoomState(rawRoomState);
        if (rawRoomState !== null && rawRoomState !== undefined && nextRoomState === null) {
          throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
        }
        roomState = nextRoomState;
        for (const write of writes) {
          const data = write.kind === 'character' && roomState && write.data && typeof write.data === 'object' && !Array.isArray(write.data)
            ? { ...(write.data as Record<string, unknown>), floor: roomState.floor }
            : write.data;
          cache.set(slot(write.kind, characterId), { version: write.expectedVersion + 1, data: structuredClone(data) });
        }
      });
    },

    /**
     * Character + Session (+ Runtime) + index as ONE `player.save`. The server
     * commits a save's writes all-or-nothing, so a play action can no longer be
     * half-saved and the engine needs no journal for it. A refusal (someone
     * else saved first) changes nothing here or on the server.
     */
    writeTogether(batch) {
      return enqueue(async () => {
        const entries: { kind: SheetsKind; key: string; data: unknown }[] = [
          { kind: 'character', key: batch.characterId, data: batch.character },
          { kind: 'session', key: batch.characterId, data: batch.session },
        ];
        if (batch.runtime !== undefined) entries.push({ kind: 'runtime', key: batch.characterId, data: batch.runtime });
        entries.push({ kind: 'index', key: 'index', data: batch.index });
        const writes = entries.map((entry) => ({
          op: 'put' as const, ...entry,
          expectedVersion: cache.get(slot(entry.kind, entry.key))?.version ?? 0,
        }));
        const { status, json } = await send({ action: 'player.save', writes });
        if (status !== 200) throw failure(status, json);
        const rawRoomState = record(json).roomState;
        const nextRoomState = parseRoomState(rawRoomState);
        if (rawRoomState !== null && rawRoomState !== undefined && nextRoomState === null) {
          throw new SheetsSaveError('The sheet server sent unreadable campaign state.', 'server', status);
        }
        roomState = nextRoomState;
        for (const write of writes) {
          const data = write.kind === 'character' && roomState && write.data && typeof write.data === 'object' && !Array.isArray(write.data)
            ? { ...(write.data as Record<string, unknown>), floor: roomState.floor }
            : write.data;
          cache.set(slot(write.kind, write.key), { version: write.expectedVersion + 1, data: structuredClone(data) });
        }
      });
    },

    async listCharacterIds() {
      if (!loaded) throw new Error('Sheet storage was read before load() finished.');
      const prefix = slot('character', '');
      return [...cache.keys()].filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length));
    },
    async readCharacter(id) { return read('character', id); },
    writeCharacter(id, data) { return put('character', id, data); },
    deleteCharacter(id) { return remove('character', id); },

    async readSession(characterId) { return read('session', characterId); },
    writeSession(characterId, data) { return put('session', characterId, data); },
    deleteSession(characterId) { return remove('session', characterId); },

    async readJournal(characterId) { return read('journal', characterId); },
    writeJournal(characterId, data) { return put('journal', characterId, data); },
    deleteJournal(characterId) { return remove('journal', characterId); },

    async readRuntime(characterId) { return read('runtime', characterId); },
    writeRuntime(characterId, data) { return put('runtime', characterId, data); },
    deleteRuntime(characterId) { return remove('runtime', characterId); },

    async readIndex() { return read('index', 'index'); },
    writeIndex(data) { return put('index', 'index', data); },

    async readCustomContent(name) { return read('custom', name); },
    writeCustomContent(name, data) { return put('custom', name, data); },

    async describeLocation() { return 'Sheet server (your player account)'; },
  };
}
